import { validateUsername as validateUsernameFormat } from "./usernameValidation";

/** External availability flags (from API debounce checks). Pure: no hooks. */
export type ProfileAvailabilityFlags = {
  usernameTaken?: boolean;
  mobileTaken?: boolean;
};

export type ProfileFormValidationData = {
  username: string;
  firstName: string;
  lastName: string;
  artistName: string;
  spotifyUrl: string;
  mobile: string;
  address: string;
};

export type ProfileFormValidationErrors = {
  username: string;
  firstName: string;
  lastName: string;
  artistName: string;
  spotifyUrl: string;
  mobile: string;
  address: string;
};

const SPOTIFY_URL_PATTERN = /^https:\/\/open\.spotify\.com\/artist\/[a-zA-Z0-9]+.*$/;
const SPOTIFY_URI_PATTERN = /^spotify:artist:[a-zA-Z0-9]+$/;
const SPOTIFY_URL_ERROR =
  "Enter a Spotify artist link with artist ID (e.g. https://open.spotify.com/artist/4Z8W4fKeB5YxbusRsdQVPb or spotify:artist:4Z8W4fKeB5YxbusRsdQVPb).";

/**
 * Field-level validation for artist/profile form. Pure function.
 */
export function validateField(
  name: string,
  value: string,
  availability: ProfileAvailabilityFlags = {}
): string {
  const { usernameTaken = false, mobileTaken = false } = availability;

  switch (name) {
    case "username": {
      const validation = validateUsernameFormat(value);
      if (!validation.isValid) return validation.error || "Invalid username";
      if (usernameTaken) return "Username is already taken";
      return "";
    }
    case "firstName":
      if (!value.trim()) return "First name is required";
      if (value.length < 2) return "First name must be at least 2 characters";
      return "";
    case "lastName":
      if (value.trim() && value.trim().length < 2)
        return "Last name must be at least 2 characters";
      return "";
    case "artistName":
      if (!value.trim()) return "Artist name is required";
      if (value.length < 2) return "Artist name must be at least 2 characters";
      return "";
    case "spotifyUrl":
      if (value) {
        const trimmed = value.trim();
        if (!SPOTIFY_URL_PATTERN.test(trimmed) && !SPOTIFY_URI_PATTERN.test(trimmed)) {
          return SPOTIFY_URL_ERROR;
        }
      }
      return "";
    case "mobile":
      if (!value.trim()) return "Mobile number is required";
      const cleanNumber = value.replace(/\D/g, "");
      if (cleanNumber.length < 10) return "Mobile number must be at least 10 digits";
      if (cleanNumber.length > 12) return "Mobile number cannot exceed 12 digits";
      if (mobileTaken) return "Mobile number is already registered";
      return "";
    case "address":
      if (value && value.trim()) {
        const addressPattern = /^[a-zA-Z0-9\s\-,.#\/]+$/;
        if (!addressPattern.test(value)) {
          return "Address can only contain letters, numbers, spaces, and the characters: ( ) , . / - #";
        }
      }
      return "";
    default:
      return "";
  }
}

/**
 * Full profile form validation (same fields as ArtistProfile save). Pure function.
 */
export function validateProfileForm(
  data: ProfileFormValidationData,
  availability: ProfileAvailabilityFlags = {}
): { errors: ProfileFormValidationErrors; isValid: boolean } {
  const errors: ProfileFormValidationErrors = {
    username: validateField("username", data.username, availability),
    firstName: validateField("firstName", data.firstName, availability),
    lastName: validateField("lastName", data.lastName, availability),
    artistName: validateField("artistName", data.artistName, availability),
    spotifyUrl: validateField("spotifyUrl", data.spotifyUrl, availability),
    mobile: validateField("mobile", data.mobile, availability),
    address: validateField("address", data.address, availability),
  };
  const isValid = !Object.values(errors).some((e) => e !== "");
  return { errors, isValid };
}
